import React from 'react';

/**
 * Input component with label, error text, and icon slots
 * @param {Object} props
 * @param {string} props.label
 * @param {string} props.error
 * @param {React.ReactNode} props.leftIcon
 * @param {React.ReactNode} props.rightIcon
 * @param {string} props.id
 * @param {string} props.className
 */
export const Input = React.forwardRef(({
  label,
  error,
  leftIcon,
  rightIcon,
  id,
  className = '',
  ...props
}, ref) => {
  const hasError = !!error;
  
  return (
    <div className="w-full">
      {label && (
        <label 
          htmlFor={id} 
          className="block text-sm font-medium text-brand-text mb-1.5"
        >
          {label}
          {props.required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" aria-hidden="true">
            {leftIcon}
          </div>
        )}
        <input
          ref={ref}
          id={id}
          className={`
            block w-full rounded-xl border bg-brand-bg text-brand-text placeholder-brand-muted
            transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-brand-bg
            disabled:opacity-50 disabled:cursor-not-allowed
            ${hasError 
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
              : 'border-zinc-800 focus:border-brand-gold focus:ring-brand-gold'
            }
            ${leftIcon ? 'pl-10' : 'px-3'}
            ${rightIcon ? 'pr-10' : 'px-3'}
            ${!leftIcon && !rightIcon ? 'px-3' : ''}
            py-2
            ${className}
          `}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${id}-error` : undefined}
          {...props}
        />
        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted" aria-hidden="true">
            {rightIcon}
          </div>
        )}
      </div>
      {error && (
        <p 
          id={`${id}-error`} 
          className="mt-1.5 text-sm text-red-500"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';
